import { FC, useEffect } from 'react'
import { Box, Typography } from '@mui/material'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { LAYOUT } from '@/shared/styles/spacing'
import { typeScale } from '@/shared/styles/typography'
import { GuideVariant } from './core'
import { GuideToc, TOC_WIDTH } from './components/GuideToc'
import { GuideRouter } from './components/GuideRouter'
import { MarkdownBody } from './components/MarkdownBody'
import { SectionSteps } from './components/SectionSteps'

/**
 * Kakeru君 困ったときガイド (/guide)。
 *
 * - 未認証で開ける (useCheckAuthQuery の isPublicPage)。「ログインできない」区分を含むため。
 * - サイドバーの［困ったときガイド］と、紙の店舗シートの QR から来る (別タブ)。
 * - アプリのシェル (Sidebar) は被せない。ログイン前後どちらでも同じ 1 枚として開ける
 *   ことを優先する。theme は RouterShell の ThemeProvider をそのまま使う。
 * - 法人別の連絡先は props (GuideVariant) で受ける。どの法人に何を出すかは
 *   App.tsx の route と variants/ が持つ。
 *
 * まもる君の /guide (prescription-management-front src/pages/guide) と同じ作り。
 * 28 spec: 罫線で区切らない。見出しは title-page(40) → 区分 h2(24) → 手順 h4(16) の
 * 3 段のみ。区分の上に 64px、手順の上に 32px と、余白を倍で振って境目を作る。
 */
export const KakeruGuidePage: FC<GuideVariant> = ({ sections, contact }) => {
  // 紙の QR・受電の台本は /guide#b-1 のように手順を直接指す。
  // 初回表示では本文の描画がブラウザの hash ジャンプに間に合わないので、
  // 描画後に自分で送る (ページ内のリンクはブラウザ任せで動く)。
  useEffect(() => {
    const id = window.location.hash.slice(1)
    if (!id) return
    document.getElementById(id)?.scrollIntoView()
  }, [])

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: colorTokens.bg.default,
        px: { xs: 2, sm: 3 },
        py: { xs: 4, md: 5 },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: 6,
          justifyContent: 'center',
          alignItems: 'flex-start',
          mx: 'auto',
          maxWidth: LAYOUT.contentMax + TOC_WIDTH + 48,
        }}
      >
        <GuideToc sections={sections} />

        <Box sx={{ width: '100%', maxWidth: LAYOUT.contentMax, minWidth: 0 }}>
          <Typography
            component="h1"
            sx={{ ...typeScale.titlePage, mb: 1, wordBreak: 'keep-all' }}
          >
            Kakeru君 困ったときガイド
          </Typography>
          <Typography sx={{ ...typeScale.body, color: colorTokens.fg.muted }}>
            作業がどこで止まったかで区分（A〜D）を選び、区分の中では当てはまる症状の項を選んでください。
            項の中の手順は上から順に試して、直ったところで止めてください。
            A〜Dは毎日の操作の順（起動 → 患者選択 → 録音 →
            SOAP生成）に並んでいます。
          </Typography>

          <GuideRouter sections={sections} />

          {sections.map((s) => (
            <Box key={s.id} component="section">
              {/* id は見出しに置く。目次の scroll-spy が「薄い見出しが帯に
                入ったか」で現在地を決めるため、節の箱には置けない */}
              <Typography
                component="h2"
                id={s.id}
                sx={{
                  ...typeScale.h2,
                  mt: 8,
                  mb: 2,
                  scrollMarginTop: '32px',
                }}
              >
                {s.label ? `${s.label}. ${s.title}` : s.title}
              </Typography>
              {/* スマホは左の目次が出ないので、入った区分の手順だけをここに出す */}
              <SectionSteps steps={s.steps} />
              {/* 連絡先は法人で違うため md ではなく variant が持つ。
                「どこへ」(連絡先) → 「何を」(md の伝達項目) の順 */}
              {s.id === 'escalation' && contact}
              <MarkdownBody>{s.body}</MarkdownBody>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  )
}
